import React, { useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Settings } from 'lucide-react'
import {
    DEFAULT_VIDEO_PLAYBACK_RATES,
    formatPlaybackRateLabel,
    normalizePlaybackRate,
    normalizePlaybackRates,
} from 'lib/videoPlaybackRates'
import CaptionsIcon from './icons/CaptionsIcon'
import styles from './video-settings-menu.module.scss'

const HOVER_MENU_QUERY = '(hover: hover) and (pointer: fine)'

const canHoverMenu = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(HOVER_MENU_QUERY).matches

const stopEvent = event => {
    event.preventDefault()
    event.stopPropagation()
}

/**
 * The gear on a video's controls: a flyout with the playback speeds and, when
 * the runtime can show them, a CC toggle. Shared by the post's video chrome
 * and the dock's cinema transport so both offer the same settings.
 *
 * On fine pointers the flyout follows hover and keyboard focus, as the
 * volume flyouts do; on touch there is no hover, so the gear opens and
 * closes it explicitly and a tap outside closes it. Every click inside stops
 * short of the surface underneath — the picture is the play/pause control.
 *
 * @param {{
 *   playbackRate?: number,
 *   playbackRates?: number[],
 *   onPlaybackRateChange?: (rate: number) => void,
 *   captionsAvailable?: boolean,
 *   captionsEnabled?: boolean,
 *   onToggleCaptions?: () => void,
 *   className?: string,
 *   buttonClassName?: string,
 *   iconClassName?: string,
 *   popoverClassName?: string,
 *   onMouseLeave?: (event: React.MouseEvent) => void,
 *   onOpenChange?: (open: boolean) => void,
 *   dataTestId?: string,
 * }} props
 */
export default function VideoSettingsMenu({
    playbackRate = 1,
    playbackRates = DEFAULT_VIDEO_PLAYBACK_RATES,
    onPlaybackRateChange = undefined,
    captionsAvailable = false,
    captionsEnabled = false,
    onToggleCaptions = undefined,
    className = undefined,
    buttonClassName = undefined,
    iconClassName = undefined,
    popoverClassName = undefined,
    onMouseLeave = undefined,
    onOpenChange = undefined,
    dataTestId = 'video-settings',
}) {
    const rootRef = useRef(null)
    const buttonRef = useRef(null)
    const [usesHoverMenu, setUsesHoverMenu] = useState(canHoverMenu)
    const [isOpen, setIsOpen] = useState(false)
    const [isDismissed, setIsDismissed] = useState(false)
    const explicitOpen = !usesHoverMenu && isOpen

    // The chrome around the menu pins itself open while a tap-opened menu is
    // up, the way it does for the touch volume slider — otherwise its
    // auto-hide would pull the menu out from under the finger.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange
    const reportedOpenRef = useRef(false)
    useEffect(() => {
        if (reportedOpenRef.current === explicitOpen) return
        reportedOpenRef.current = explicitOpen
        onOpenChangeRef.current?.(explicitOpen)
    }, [explicitOpen])
    useEffect(
        () => () => {
            if (reportedOpenRef.current) onOpenChangeRef.current?.(false)
        },
        [],
    )

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return undefined

        const mediaQuery = window.matchMedia(HOVER_MENU_QUERY)
        const syncHoverMode = () => {
            setUsesHoverMenu(mediaQuery.matches)
            setIsOpen(false)
            setIsDismissed(false)
        }

        syncHoverMode()
        mediaQuery.addEventListener?.('change', syncHoverMode)
        return () => mediaQuery.removeEventListener?.('change', syncHoverMode)
    }, [])

    // Capture, because the chrome around this stops pointer events on the
    // way back up.
    useEffect(() => {
        if (!isOpen || typeof document === 'undefined') return undefined

        const closeOnOutsidePointer = event => {
            if (rootRef.current?.contains(event.target)) return
            setIsOpen(false)
        }

        document.addEventListener('pointerdown', closeOnOutsidePointer, true)
        return () => document.removeEventListener('pointerdown', closeOnOutsidePointer, true)
    }, [isOpen])

    const safePlaybackRate = normalizePlaybackRate(playbackRate) ?? 1
    const availablePlaybackRates = useMemo(
        () => normalizePlaybackRates(playbackRates, safePlaybackRate),
        [playbackRates, safePlaybackRate],
    )
    const showPlaybackRate =
        typeof onPlaybackRateChange === 'function' && availablePlaybackRates.length > 1
    const showCaptions = captionsAvailable && typeof onToggleCaptions === 'function'
    if (!showPlaybackRate && !showCaptions) return null

    const handleButtonClick = event => {
        stopEvent(event)
        if (usesHoverMenu) {
            setIsDismissed(false)
            return
        }
        setIsOpen(open => !open)
    }

    const handleKeyDown = event => {
        if (event.key !== 'Escape') return

        const menuOpen = usesHoverMenu ? !isDismissed : isOpen
        if (!menuOpen) return

        stopEvent(event)
        if (usesHoverMenu) setIsDismissed(true)
        else setIsOpen(false)
        buttonRef.current?.focus()
    }

    const handleMouseLeave = event => {
        // Hiding an absolutely positioned hover menu can itself move the
        // pointer outside this root. Keep the restored gear focus in that
        // case; a fresh hover or focus cycle will make the menu available.
        if (usesHoverMenu && isDismissed) return
        onMouseLeave?.(event)
    }

    const handleBlur = event => {
        if (usesHoverMenu && !event.currentTarget.contains(event.relatedTarget)) {
            setIsDismissed(false)
        }
    }

    return (
        <span
            ref={rootRef}
            className={cn(styles.control, className)}
            data-open={explicitOpen ? 'true' : 'false'}
            data-testid={dataTestId}
            onMouseMove={() => setIsDismissed(false)}
            onMouseLeave={handleMouseLeave}
            onBlurCapture={handleBlur}
            onKeyDown={handleKeyDown}
        >
            <button
                ref={buttonRef}
                type="button"
                className={buttonClassName}
                onClick={handleButtonClick}
                aria-label="Settings"
                title="Settings"
                aria-haspopup="menu"
                aria-expanded={usesHoverMenu ? undefined : explicitOpen}
            >
                <Settings className={iconClassName} aria-hidden="true" />
            </button>
            <span
                className={cn(styles.popover, popoverClassName)}
                role="menu"
                aria-label="Settings"
                onClick={stopEvent}
                hidden={usesHoverMenu && isDismissed}
                style={usesHoverMenu && isDismissed ? { display: 'none' } : undefined}
            >
                {showCaptions ? (
                    <>
                        <button
                            type="button"
                            className={cn(
                                styles.item,
                                styles.captionsItem,
                                captionsEnabled && styles.itemActive,
                            )}
                            role="menuitemcheckbox"
                            aria-checked={captionsEnabled}
                            aria-label="Captions"
                            title={captionsEnabled ? 'Turn captions off' : 'Turn captions on'}
                            onClick={event => {
                                stopEvent(event)
                                onToggleCaptions()
                            }}
                        >
                            <CaptionsIcon className={styles.captionsIcon} aria-hidden="true" />
                            <span aria-hidden="true">CC</span>
                        </button>
                        {showPlaybackRate ? (
                            <span className={styles.divider} role="separator" />
                        ) : null}
                    </>
                ) : null}
                {showPlaybackRate
                    ? availablePlaybackRates.map(rate => {
                          const active = Math.abs(rate - safePlaybackRate) < 0.001

                          return (
                              <button
                                  type="button"
                                  key={rate}
                                  className={cn(styles.item, active && styles.itemActive)}
                                  role="menuitemradio"
                                  aria-checked={active}
                                  onClick={event => {
                                      stopEvent(event)
                                      onPlaybackRateChange(rate)
                                      setIsOpen(false)
                                  }}
                              >
                                  {formatPlaybackRateLabel(rate)}
                              </button>
                          )
                      })
                    : null}
            </span>
        </span>
    )
}
